/**
 * Notification Settings Component
 *
 * Allows users to manage push notification preferences.
 * Designed for mobile-first experience.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { Notifications as NotificationIcon } from '@mui/icons-material';
import { notificationService } from '../../services/notificationService';

export function NotificationSettings() {
  const [permission, setPermission] = useState<NotificationPermission>(
    notificationService.getPermissionStatus()
  );
  const [enabled, setEnabled] = useState(notificationService.isEnabled());

  // Check permission status on mount
  useEffect(() => {
    const checkPermission = () => {
      const currentPermission = notificationService.getPermissionStatus();
      setPermission(currentPermission);
      setEnabled(notificationService.isEnabled());
    };

    // Check initially
    checkPermission();

    // Check periodically in case user changes browser settings
    const interval = setInterval(checkPermission, 2000);

    return () => clearInterval(interval);
  }, []);

  const handleRequestPermission = async () => {
    const granted = await notificationService.requestPermission();
    setPermission(notificationService.getPermissionStatus());
    setEnabled(granted);
  };

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnabled = event.target.checked;
    if (newEnabled) {
      notificationService.enable();
    } else {
      notificationService.disable();
    }
    setEnabled(newEnabled);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <NotificationIcon fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>
      </Box>

      {permission === 'default' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Get notified when your responses are ready.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRequestPermission}
            fullWidth
          >
            Enable Notifications
          </Button>
        </>
      )}

      {permission === 'denied' && (
        <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
          Notifications are blocked. Please enable them in your browser settings.
        </Alert>
      )}

      {permission === 'granted' && (
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={handleToggle}
              size="small"
            />
          }
          label={
            <Typography variant="body2">
              {enabled ? 'Enabled' : 'Disabled'}
            </Typography>
          }
        />
      )}
    </Box>
  );
}
